import { Nullable } from 'src/types/nullable.type';
import create from 'zustand';
import lscache from 'lscache';
import { getInsertionBoundary } from './helpers/block/get-insertion-boundary';
import { generateBlockId } from './helpers/generate-block-id';
import {
  AspectRatio,
  Block,
  BlockContent,
  BlockGrid,
  BlockVerticalAlignment,
  Boundary,
  BoundaryParams,
  JNode,
  Rect,
  RenderElement,
  Section,
  SectionGrid,
  Size,
} from './types';
import { filterFullRowBoundaries } from './layout-manager/filter-full-row-boundaries';
import produce from 'immer';
import { useEditorContext } from 'src/store/editor';
import { BlockLayoutExtraParams, DEFAULT_LAYOUT_CONFIG, LayoutConfig, LayoutManager } from './layout-manager';
import { cloneDeep, omit } from 'lodash';
import { sync } from './editor-sync';
import { createPerformanceLogger } from 'src/utils/performance-logger';
import { getInsertionBoundaryForBlock } from './layout-manager/get-insertion-boundary-for-block';
import { ActivityStrongIcon } from 'src/monet/icons';
import { getNumberOfRowsInSection } from './layout-manager/get-number-of-rows-in-section';

export type Step = {
  name: string;
  id: string;
};

export type DragPosition = {
  x: number;
  y: number;
};

export type MousePosition = {
  clientX: number;
  clientY: number;
};

export type DraggableState = {
  position: DragPosition;
  isDragging: boolean;
};

type ContextMenuState = {
  entity:
    | {
        type: 'block';
        blockId: Block['id'];
      }
    | { type: 'section-card'; sectionId: Section['id']; renameAction: () => void }
    | {
        type: 'section-divider';
        previousSectionId: Nullable<Section['id']>;
        nextSectionId: Nullable<Section['id']>;
        id: string;
      };
  pageX: number;
  pageY: number;
};

type Coords = {
  x: number;
  y: number;
};

type StateVars = {
  layout: {
    // blocks: Block[];
    // blockRects: { [id: Block['id']]: Rect };
    // blockGrid: Nullable<BlockGrid>;
    sections: Section[];
    sectionGrids: SectionGrid[];
    sectionRects: Rect[];
    renderElements: RenderElement[];
    boundaries: Boundary[];
    layoutReady: boolean;
    innerActualWidth: Nullable<number>;
    innerActualHeight: Nullable<number>;
    layoutConfig: LayoutConfig;
  };
  lastActionWasUndoOrRedo: boolean;
  jdoc: Nullable<JNode>;
  latestTrackableJdoc: Nullable<JNode>;
  latestTrackableSections: Nullable<Section[]>;
  undoStack: UserEditorAction[][];
  redoStack: UserEditorAction[][];
  selectedBlockId: Nullable<Block['id']>;
  currentSectionId: Nullable<Section['id']>;
  scrollToSectionId: Nullable<Section['id']>;
  selectionCoords: Nullable<Coords>;
  editorContentElement: Nullable<HTMLElement>;
  scrollToBlockId: Nullable<Block['id']>;
  contextMenuState: Nullable<ContextMenuState>;
  _draggableId: Nullable<string>;
  position: DragPosition;
  isDragging: boolean;
  insertionBoundary: Nullable<Boundary>;
  appendingSection: boolean;
  editorContentRect: Nullable<Rect>;
  outerAreaRect: Nullable<Rect>;
  innerAreaRect: Nullable<Rect>;
  canvasScrollPosition: { x: number; y: number };
  online: boolean;
};

type ResizeHorizontalAction = {
  type: 'resize-horizontal';
  boundary: Boundary;
  boundaryX: number;
};

type ResizeVerticalAction = {
  type: 'resize-vertical';
  boundary: Boundary;
  bottomBoundaryY: number;
};

type ReplaceBlockAction = {
  type: 'replace-block';
  id: Block['id'];
  content: Block['content'];
  layoutExtraParams: BlockLayoutExtraParams;
};

type UserEditorAction =
  | {
      type: 'append-block';
      content: Block['content'];
      id: Block['id'];
      layoutExtraParams: BlockLayoutExtraParams;
    }
  | {
      type: 'append-section';
      content?: Nullable<Block['content']>;
      id: Section['id'];
    }
  | {
      type: 'delete-section';
      id: Section['id'];
    }
  | {
      type: 'undelete-section';
      section: Section;
      index: number;
    }
  | {
      type: 'move-section';
      id: Section['id'];
      newIndex: number;
    }
  | {
      type: 'merge-sections';
      previousSectionId: Section['id'];
      nextSectionId: Section['id'];
    }
  | {
      type: 'unmerge-sections';
      previousSectionId: Section['id'];
      previousSectionBlocks: Block[];
      nextSection: Section;
    }
  | {
      type: 'insert-section-at-boundary';
      id: Section['id'];
      boundary: Boundary;
    }
  | {
      type: 'uninsert-section-at-boundary';
      id: Section['id'];
      boundary: Boundary;
      joinSections: boolean;
    }
  | {
      type: 'move-section-divider-to-boundary';
      previousSectionId: Nullable<Section['id']>;
      nextSectionId: Nullable<Section['id']>;
      boundaryParams: BoundaryParams;
    }
  | {
      type: 'insert-block-at-boundary';
      id: Block['id'];
      content: Block['content'];
      layoutExtraParams: BlockLayoutExtraParams;
      boundary: Boundary;
    }
  | {
      type: 'move-block-to-boundary';
      id: Block['id'];
      boundaryParams: BoundaryParams;
    }
  | {
      type: 'set-block-vertical-alignment';
      id: Block['id'];
      verticalAlignment: BlockVerticalAlignment;
    }
  | ReplaceBlockAction
  | {
      type: 'set-section-name';
      id: Section['id'];
      name: NonNullable<Section['name']>;
    }
  | {
      type: 'update-block-content';
      id: Block['id'];
      content: Partial<Block['content']>;
    }
  | {
      type: 'delete-block';
      id: Block['id'];
    }
  | {
      type: 'set-step-blocks';
      json: string;
      id: Section['id'];
    }
  | ResizeHorizontalAction
  | ResizeVerticalAction;

type ActionSource = 'undo' | 'redo' | 'none';

type StateFns = {
  undo: () => void;
  redo: () => void;
  selectBlock: (blockId: Block['id'], coords?: Coords) => void;
  clearSelectedBlock: () => void;
  setCurrentSectionId: (sectionId: Section['id']) => void;
  setScrollToSectionId: (sectionId: Nullable<Section['id']>) => void;
  setEditorContentElement: (element: HTMLElement) => void;
  setScrollToBlockId: (blockId: Block['id']) => void;
  keepDragging: ({
    draggableId,
    position,
    dragElementRect,
    mousePosition,
  }: {
    draggableId: string;
    position: DragPosition;
    mousePosition: MousePosition;
    dragElementRect: Rect;
  }) => void;
  endDragging: ({
    draggableId,
    blockContent,
    mousePosition,
    dragElementRect,
  }: {
    draggableId: string;
    blockContent: BlockContent;
    mousePosition: MousePosition;
    dragElementRect: Rect;
  }) => void;
  resetDragging: () => void;
  setCanvasScrollPosition: (x: number, y: number) => void;
  setContainerRects: (rects: { editorContentRect: Rect; outerAreaRect: Rect; innerAreaRect: Rect }) => void;
  setBlockContentSize: (blockId: Block['id'], size: Size) => void;
  setContextMenuState: (state: ContextMenuState) => void;
  clearContextMenuState: () => void;
  dispatchUserEditorAction: (
    userEditorAction: UserEditorAction,
    config?: { source?: ActionSource; undoable?: boolean; syncable?: boolean }
  ) => Promise<any>;
  mapToServerAction: (userEditorAction: UserEditorAction, oldState: any, newState: any, source: ActionSource) => any;
  initEditor: (jDoc: JNode) => void;
  clearLastActionWasUndoOrRedo: () => void;
  resetEditor(): void;
  setOffline(): void;
  setOnline(): void;
};

type State = StateVars & StateFns;

export function createDefaultSection(): JNode {
  return {
    type: { name: 'section' },
    attributes: {
      id: generateBlockId(),
    },
    content: [
      {
        type: { name: 'block' },
        attributes: {
          id: generateBlockId(),
          content: {
            type: 'text',
            value: '<h1></h1>',
          },
          layout: {
            spec: {
              type: 'calculated-height',
              width: '100%',
              height: null,
              minHeight: 0,
              contentSize: null,
            },
            verticalAlign: 'center',
          },
        },
        content: [],
      },
    ],
  };
}

function updateJDocWithBlocks(jdoc: JNode, sections: Section[]): JNode {
  return produce(jdoc, (jdoc) => {
    jdoc.content = sections.map((section) => {
      return {
        type: {
          name: 'section',
        },
        attributes: {
          id: section.id,
        },
        content: section.blocks.map((block) => {
          let blockContent = block.content;
          if (blockContent.type === 'image' && blockContent.file) {
            blockContent = omit(blockContent, 'file');
          }
          return {
            type: {
              name: 'block',
            },
            attributes: {
              id: block.id,
              content: cloneDeep(blockContent),
              layout: cloneDeep(block.layout),
            },
            content: [],
          };
        }),
      };
    });
  });
}

export function jDocToBlocks(jDoc: JNode): Section[] {
  // console.log('jDocToBlocks', jDoc);
  return jDoc.content.map((jNode: JNode) => {
    return {
      id: jNode.attributes.id,
      name: jNode.attributes.name || null,
      blocks: jNode.content.map((jNode: JNode) => {
        return {
          id: jNode.attributes.id,
          content: jNode.attributes.content as Block['content'],
          layout: jNode.attributes.layout as Block['layout'],
        };
      }),
    };
  });
}

function blockToServerBlock(block: Block): Block {
  let blockContent = block.content;
  if (blockContent.type === 'image' && blockContent.file) {
    blockContent = omit(blockContent, 'file');
  }
  return {
    ...block,
    content: blockContent,
  };
}

function sectionToJNodes(section: Section, forServer: boolean): JNode[] {
  let sectionBlocks = section.blocks;
  if (forServer) {
    sectionBlocks = sectionBlocks.map(blockToServerBlock);
  }
  return sectionBlocks.map((b) => {
    return {
      type: { name: 'block' },
      attributes: {
        id: b.id,
        content: b.content,
        layout: b.layout,
      },
      content: [],
    };
  });
}

function findSectionIndexByBlockId(sections: Section[], id: Block['id']): Nullable<number> {
  for (let i = 0; i < sections.length; i++) {
    for (const block of sections[i].blocks) {
      if (block.id === id) {
        return i;
      }
    }
  }
  return null;
}

function findSectionByBlockId(sections: Section[], id: Block['id']): Nullable<Section> {
  const index = findSectionIndexByBlockId(sections, id);
  if (index !== null) {
    return sections[index];
  } else {
    return null;
  }
}

function findSectionIndexById(sections: Section[], id: Section['id']): Nullable<number> {
  for (let i = 0; i < sections.length; i++) {
    if (sections[i].id === id) {
      return i;
    }
  }
  return null;
}

function findSectionById(sections: Section[], id: Section['id']): Nullable<Section> {
  const index = findSectionIndexById(sections, id);
  if (index !== null) {
    return sections[index];
  } else {
    return null;
  }
}

function jNodesToBlocks(jnodes: JNode[]): Block[] {
  return jnodes.map((n) => {
    return {
      id: n.attributes.id,
      content: n.attributes.content!,
      layout: n.attributes.layout!,
    };
  });
}

function inverseAction(action: UserEditorAction, oldState: Section[], newState: Section[]): UserEditorAction[] {
  switch (action.type) {
    case 'append-block': {
      const lastSection = oldState[oldState.length - 1];
      return [
        {
          type: 'set-step-blocks',
          id: lastSection.id,
          json: JSON.stringify(sectionToJNodes(lastSection, false)),
        },
      ];
    }
    case 'insert-block-at-boundary': {
      const section = oldState[action.boundary.sectionIndex];
      return [
        {
          type: 'set-step-blocks',
          id: section.id,
          json: JSON.stringify(sectionToJNodes(section, false)),
        },
      ];
    }
    case 'move-block-to-boundary': {
      const fromSectionIndex = findSectionIndexByBlockId(oldState, action.id)!;
      const fromSection = oldState[fromSectionIndex];
      const toSectionIndex = findSectionIndexByBlockId(newState, action.id)!;
      if (fromSectionIndex === toSectionIndex) {
        return [
          {
            type: 'set-step-blocks',
            id: fromSection.id,
            json: JSON.stringify(sectionToJNodes(fromSection, false)),
          },
        ];
      } else {
        const boundaryParams = getInsertionBoundaryForBlock(fromSection, fromSectionIndex, action.id);
        return [
          {
            type: 'move-block-to-boundary',
            id: action.id,
            boundaryParams,
          },
        ];
      }
    }
    case 'resize-horizontal':
    case 'resize-vertical': {
      const section = oldState[action.boundary.sectionIndex];
      return [
        {
          type: 'set-step-blocks',
          id: section.id,
          json: JSON.stringify(sectionToJNodes(section, false)),
        },
      ];
    }
    case 'replace-block':
    case 'set-block-vertical-alignment':
    case 'update-block-content':
    case 'delete-block': {
      const section = findSectionByBlockId(oldState, action.id)!;
      return [
        {
          type: 'set-step-blocks',
          id: section.id,
          json: JSON.stringify(sectionToJNodes(section, false)),
        },
      ];
    }
    case 'set-step-blocks': {
      let section = oldState.find((s) => s.id === action.id);
      return [
        {
          type: 'set-step-blocks',
          id: action.id,
          json: JSON.stringify(sectionToJNodes(section!, false)),
        },
      ];
    }
    case 'append-section': {
      return [
        {
          type: 'delete-section',
          id: action.id,
        },
      ];
    }
    case 'insert-section-at-boundary': {
      const joinSections = newState.length - oldState.length === 2;
      return [
        {
          type: 'uninsert-section-at-boundary',
          id: action.id,
          boundary: action.boundary,
          joinSections,
        },
      ];
    }
    case 'uninsert-section-at-boundary': {
      return [
        {
          type: 'insert-section-at-boundary',
          id: action.id,
          boundary: action.boundary,
        },
      ];
    }
    case 'move-section-divider-to-boundary': {
      return [];
    }
    case 'delete-section': {
      const sectionIndex = findSectionIndexById(oldState, action.id)!;
      const section = oldState[sectionIndex];
      return [
        {
          type: 'undelete-section',
          section: section,
          index: sectionIndex,
        },
      ];
    }
    case 'undelete-section': {
      return [
        {
          type: 'delete-section',
          id: action.section.id,
        },
      ];
    }
    case 'move-section': {
      const sectionIndex = findSectionIndexById(oldState, action.id)!;
      return [
        {
          type: 'move-section',
          id: action.id,
          newIndex: sectionIndex,
        },
      ];
    }
    case 'merge-sections': {
      const previousSectionIndex = findSectionIndexById(oldState, action.previousSectionId)!;
      const previousSection = oldState[previousSectionIndex];
      const nextSectionIndex = findSectionIndexById(oldState, action.nextSectionId)!;
      const nextSection = oldState[nextSectionIndex];
      return [
        {
          type: 'unmerge-sections',
          previousSectionId: previousSection.id,
          previousSectionBlocks: previousSection.blocks,
          nextSection: nextSection,
        },
      ];
    }
    case 'unmerge-sections': {
      return [
        {
          type: 'merge-sections',
          previousSectionId: action.previousSectionId,
          nextSectionId: action.nextSection.id,
        },
      ];
    }
    case 'set-section-name': {
      const oldSection = findSectionById(oldState, action.id)!;
      return [
        {
          type: 'set-section-name',
          id: action.id,
          name: oldSection.name || 'Untitled',
        },
      ];
    }
    default: {
      const exhaustiveCheck: never = action;
      throw new Error(`Unhandled case: ${exhaustiveCheck}`);
    }
  }
}

function createInitialState(): StateVars {
  return {
    layout: {
      sections: [],
      sectionGrids: [],
      renderElements: [],
      sectionRects: [],
      boundaries: [],
      layoutReady: false,
      innerActualWidth: null,
      innerActualHeight: null,
      layoutConfig: DEFAULT_LAYOUT_CONFIG,
    },
    jdoc: null,
    lastActionWasUndoOrRedo: false,
    latestTrackableJdoc: null,
    latestTrackableSections: null,
    undoStack: [],
    redoStack: [],
    selectedBlockId: null,
    currentSectionId: null,
    selectionCoords: null,
    scrollToBlockId: null,
    scrollToSectionId: null,
    editorContentElement: null,
    contextMenuState: null,
    _draggableId: null,
    position: { x: 0, y: 0 },
    isDragging: false,
    insertionBoundary: null,
    appendingSection: false,
    editorContentRect: null,
    outerAreaRect: null,
    innerAreaRect: null,
    canvasScrollPosition: { x: 0, y: 0 },
    online: true,
  };
}

const lm = new LayoutManager();
lm.addListener('update', () => {
  // console.log('lm update');
  const { outputs } = lm;
  if (!outputs) {
    return;
  }
  // console.log('lm update sections', lm.sections);
  useEditorStore.setState(
    produce(useEditorStore.getState(), (state) => {
      state.layout = {
        renderElements: lm.renderElements,
        sections: lm.sections,
        sectionGrids: lm.sectionGrids,
        sectionRects: outputs.sectionRects,
        // blockRects: lm.blockRects,
        // blockGrid: lm.blockGrid,
        boundaries: lm.boundaries,
        layoutReady: true,
        innerActualWidth: outputs.innerActualWidth,
        innerActualHeight: outputs.innerActualHeight,
        layoutConfig: lm.layoutConfig,
      };
    })
  );
});

export const useEditorStore = create<State>((set, get) => ({
  ...createInitialState(),
  undo: () => {
    const { undoStack, dispatchUserEditorAction } = get();
    // console.log('UNDO', undoStack);
    if (undoStack.length > 0) {
      const actions = undoStack[undoStack.length - 1];
      set({ undoStack: undoStack.slice(0, undoStack.length - 1) });
      for (const action of actions) {
        dispatchUserEditorAction(action, { source: 'undo' });
      }
    }
  },
  redo: () => {
    const { redoStack, dispatchUserEditorAction } = get();
    // console.log('REDO', redoStack);
    if (redoStack.length > 0) {
      const actions = redoStack[redoStack.length - 1];
      set({ redoStack: redoStack.slice(0, redoStack.length - 1) });
      for (const action of actions) {
        dispatchUserEditorAction(action, { source: 'redo' });
      }
    }
  },
  selectBlock: (blockId: string, coords?: Coords) => {
    set({ selectedBlockId: blockId, selectionCoords: coords || null });
  },
  clearSelectedBlock: () => {
    set({ selectedBlockId: null });
  },
  setEditorContentElement: (editorContentElement: HTMLElement) => {
    set({ editorContentElement });
  },
  setCurrentSectionId: (currentSectionId: string) => {
    set({ currentSectionId });
  },
  setScrollToSectionId: (scrollToSectionId: Nullable<string>) => {
    set({ scrollToSectionId });
  },
  setScrollToBlockId: (scrollToBlockId: string) => {
    set({ scrollToBlockId });
  },
  keepDragging: ({
    draggableId,
    position,
    dragElementRect,
    mousePosition,
  }: {
    draggableId: string;
    position: DragPosition;
    mousePosition: MousePosition;
    dragElementRect: Rect;
  }) => {
    // const logPerf = createPerformanceLogger('keepDragging');
    const { layout, editorContentRect, outerAreaRect, innerAreaRect, canvasScrollPosition, clearSelectedBlock } = get();
    const { sectionGrids, boundaries, renderElements } = layout;
    // logPerf('clearing selected block');
    clearSelectedBlock();
    // logPerf('cleared selected block');
    if (editorContentRect && outerAreaRect && innerAreaRect) {
      const x = mousePosition.clientX + canvasScrollPosition.x - innerAreaRect.x;
      const y = mousePosition.clientY + canvasScrollPosition.y - innerAreaRect.y;
      const lastDivider = renderElements.find((re) => re.type === 'divider' && re.isLast);
      if (lastDivider && y >= lastDivider.rect.y) {
        set({ appendingSection: true, insertionBoundary: boundaries.find((b) => b.forNewSection) || null });
        // logPerf('appending section');
        return;
      }
      // logPerf('checking full row boundaries');
      const fullRowBoundaries = filterFullRowBoundaries(boundaries, sectionGrids);
      // logPerf('checking insertion boundary');
      const boundary = getInsertionBoundary({
        boundaries: fullRowBoundaries,
        elementRect: dragElementRect,
        overlapRect: outerAreaRect,
        x,
        y,
      });
      // logPerf('found insertion boundary');
      set({ insertionBoundary: boundary || null, appendingSection: false });
      // logPerf('setting insertion boundary');
    }
    // logPerf('setting dragging state');
    set({ position, _draggableId: draggableId, isDragging: true });
    // logPerf('done');
  },
  endDragging: ({
    draggableId,
    blockContent,
    mousePosition,
    dragElementRect,
  }: {
    draggableId: string;
    blockContent: BlockContent;
    mousePosition: MousePosition;
    dragElementRect: Rect;
  }) => {
    const {
      selectBlock,
      editorContentRect,
      outerAreaRect,
      innerAreaRect,
      canvasScrollPosition,
      dispatchUserEditorAction,
      setScrollToBlockId,
      setCurrentSectionId,
      setScrollToSectionId,
    } = get();
    const performanceLogger = createPerformanceLogger('endDragging');
    const { boundaries, renderElements, sectionGrids } = get().layout;
    if (editorContentRect && outerAreaRect && innerAreaRect) {
      const x = mousePosition.clientX + canvasScrollPosition.x - innerAreaRect.x;
      const y = mousePosition.clientY + canvasScrollPosition.y - innerAreaRect.y;
      const lastDivider = renderElements.find((re) => re.type === 'divider' && re.isLast);
      performanceLogger('found last divider');
      if (lastDivider && y >= lastDivider.rect.y) {
        performanceLogger('appending section');
        const id = generateBlockId();
        dispatchUserEditorAction({
          type: 'append-section',
          id,
          content: blockContent,
        });
        performanceLogger('appended section');
        setCurrentSectionId(id);
        performanceLogger('current section set');
        setScrollToSectionId(id);
        performanceLogger('scroll to section set');
      } else {
        performanceLogger('inserting block');
        const boundary = getInsertionBoundary({
          boundaries: filterFullRowBoundaries(boundaries, sectionGrids),
          elementRect: dragElementRect,
          overlapRect: outerAreaRect,
          x,
          y,
        });
        performanceLogger('found insertion boundary');
        if (boundary) {
          performanceLogger('inserting block');
          const id = generateBlockId();
          dispatchUserEditorAction({
            type: 'insert-block-at-boundary',
            id,
            boundary,
            content: blockContent,
            layoutExtraParams: {},
          });
          performanceLogger('inserted block');
          selectBlock(id);
          performanceLogger('selected block');
        }
      }
    }
    performanceLogger('resetting dragging');
    set({
      isDragging: false,
      position: { x: 0, y: 0 },
      _draggableId: null,
      insertionBoundary: null,
      appendingSection: false,
    });
    performanceLogger('done');
  },
  resetDragging: () => {
    set({
      isDragging: false,
      position: { x: 0, y: 0 },
      _draggableId: null,
      insertionBoundary: null,
      appendingSection: false,
    });
  },
  setCanvasScrollPosition: (x: number, y: number) => {
    set({ canvasScrollPosition: { x, y } });
  },
  setContainerRects: ({
    editorContentRect,
    outerAreaRect,
    innerAreaRect,
  }: {
    editorContentRect: Rect;
    outerAreaRect: Rect;
    innerAreaRect: Rect;
  }) => {
    set({ editorContentRect, outerAreaRect, innerAreaRect });
    const innerAreaWidth = innerAreaRect.width;
    const innerAreaHeight = editorContentRect.height;
    const outerAreaLeftOffset = innerAreaRect.x - outerAreaRect.x;
    const outerAreaRightOffset = outerAreaRect.x + outerAreaRect.width - innerAreaRect.x - innerAreaRect.width;
    lm.setLayoutInputs({
      innerAreaWidth,
      innerAreaHeight,
      outerAreaLeftOffset,
      outerAreaRightOffset,
    });
  },
  setBlockContentSize: (blockId: string, size: Size) => {
    lm.setBlockContentSize(blockId, size);
  },
  setContextMenuState: (contextMenuState: ContextMenuState) => {
    set({ contextMenuState });
  },
  clearContextMenuState: () => {
    set({ contextMenuState: null });
  },
  dispatchUserEditorAction: (userEditorAction: UserEditorAction, config) => {
    const { undoable = true, syncable = true, source = 'none' } = config || {};

    // console.log('dispatchUserEditorAction', userEditorAction, config);
    const performanceLogger = createPerformanceLogger('dispatchUserEditorAction');
    const { type } = userEditorAction;
    const {
      mapToServerAction,
      jdoc: oldJDoc,
      layout: oldLayout,
      undoStack,
      redoStack,
      latestTrackableSections,
    } = get();
    if (!oldJDoc || !latestTrackableSections) {
      console.warn('No jdoc');
      return Promise.reject('No jdoc');
    }
    let newJDoc: JNode;
    set({ lastActionWasUndoOrRedo: source === 'undo' || source === 'redo' });
    if (type === 'set-step-blocks') {
      performanceLogger('set-step-blocks');
      newJDoc = produce(oldJDoc, (j) => {
        j.content = JSON.parse(userEditorAction.json);
      });
      const sections = produce(oldLayout.sections, (ss) => {
        for (let i = 0; i < ss.length; i++) {
          const section = ss[i];
          if (section.id === userEditorAction.id) {
            ss[i].blocks = jNodesToBlocks(JSON.parse(userEditorAction.json));
          }
        }
      });
      performanceLogger('initializing sections');
      lm.initializeSections(sections);
    } else {
      performanceLogger('other actions');
      if (type === 'set-section-name') {
        lm.updateSection(userEditorAction.id, { name: userEditorAction.name });
      } else if (type === 'append-block') {
        lm.appendBlock({
          blockId: userEditorAction.id,
          blockContent: cloneDeep(userEditorAction.content),
          layoutExtraParams: userEditorAction.layoutExtraParams,
        });
      } else if (type === 'append-section') {
        const blockContent = cloneDeep(
          userEditorAction.content || {
            type: 'text',
            value: '<h2></h2>',
            horizontalAlignment: 'left',
          }
        );

        lm.appendSection({
          sectionId: userEditorAction.id,
          newBlockParams: {
            blockId: generateBlockId(),
            blockContent: blockContent as BlockContent,
            layoutExtraParams: {},
          },
        });
      } else if (type === 'insert-block-at-boundary') {
        lm.insertBlockAtBoundary({
          blockId: userEditorAction.id,
          blockContent: userEditorAction.content,
          boundary: userEditorAction.boundary,
          layoutExtraParams: userEditorAction.layoutExtraParams,
        });
      } else if (type === 'insert-section-at-boundary') {
        lm.insertSectionAtBoundary({
          sectionId: userEditorAction.id,
          boundary: userEditorAction.boundary,
          newBlockParams: {
            blockId: generateBlockId(),
            blockContent: {
              type: 'text',
              value: '<h2></h2>',
              horizontalAlignment: 'left',
            },
            layoutExtraParams: {},
          },
        });
      } else if (type === 'uninsert-section-at-boundary') {
        lm.uninsertSectionAtBoundary({
          sectionId: userEditorAction.id,
          boundary: userEditorAction.boundary,
          joinSections: userEditorAction.joinSections,
        });
      } else if (type === 'move-section-divider-to-boundary') {
        lm.moveSectionDividerToBoundary({
          previousSectionId: userEditorAction.previousSectionId,
          nextSectionId: userEditorAction.nextSectionId,
          boundaryParams: userEditorAction.boundaryParams,
        });
      } else if (type === 'move-block-to-boundary') {
        lm.moveBlockToBoundary(userEditorAction.id, userEditorAction.boundaryParams);
      } else if (type === 'replace-block') {
        lm.replaceBlock({
          blockId: userEditorAction.id,
          blockContent: cloneDeep(userEditorAction.content),
          layoutExtraParams: userEditorAction.layoutExtraParams,
        });
      } else if (type === 'update-block-content') {
        lm.updateBlockContent(userEditorAction.id, userEditorAction.content);
      } else if (type === 'set-block-vertical-alignment') {
        lm.setBlockVerticalAlignment(userEditorAction.id, userEditorAction.verticalAlignment);
      } else if (type === 'resize-horizontal') {
        lm.resizeHorizontal(userEditorAction.boundary, userEditorAction.boundaryX);
      } else if (type === 'resize-vertical') {
        lm.resizeVertical(userEditorAction.boundary, userEditorAction.bottomBoundaryY);
      } else if (type === 'delete-block') {
        lm.deleteBlock(userEditorAction.id);
        if (get().selectedBlockId === userEditorAction.id) {
          get().clearSelectedBlock();
        }
      } else if (type === 'delete-section') {
        lm.deleteSection(userEditorAction.id);
      } else if (type === 'undelete-section') {
        lm.undeleteSection(userEditorAction.section, userEditorAction.index);
      } else if (type === 'move-section') {
        lm.moveSection({
          sectionId: userEditorAction.id,
          newSectionIndex: userEditorAction.newIndex,
        });
      } else if (type === 'merge-sections') {
        lm.mergeSections({
          previousSectionId: userEditorAction.previousSectionId,
          nextSectionId: userEditorAction.nextSectionId,
        });
      } else if (type === 'unmerge-sections') {
        lm.unmergeSections({
          previousSectionId: userEditorAction.previousSectionId,
          previousSectionBlocks: userEditorAction.previousSectionBlocks,
          nextSection: userEditorAction.nextSection,
        });
      } else {
        const exhaustiveCheck: never = userEditorAction;
        console.warn('unknown editor action type', type);
        return Promise.reject('unknown editor action type');
      }
      performanceLogger('lm update done');
      newJDoc = updateJDocWithBlocks(oldJDoc, get().layout.sections);
      performanceLogger('jdoc update done');
    }

    performanceLogger('undoable check');
    if (undoable) {
      const inverses = inverseAction(userEditorAction, latestTrackableSections, lm.sections);
      set({ latestTrackableSections: lm.sections });
      // console.log('undoStack inverse', undoStack, inverses);
      if (source === 'none') {
        set({ undoStack: [...undoStack, inverses], redoStack: [] });
      } else if (source === 'undo') {
        set({ redoStack: [...redoStack, inverses] });
      } else if (source === 'redo') {
        set({ undoStack: [...undoStack, inverses] });
      }
    }
    performanceLogger('updated undo stack');

    // console.log('undo', undoStack);
    // console.log('redo', redoStack);

    // console.log('dispatchUserEditorAction newJdoc', newJDoc);
    set({ jdoc: newJDoc });
    performanceLogger('set jdoc');

    if (syncable) {
      const serverActions = mapToServerAction(userEditorAction, oldLayout.sections, lm.sections, source);
      return sync(serverActions);
    }

    return Promise.resolve();
  },
  mapToServerAction(action: UserEditorAction, oldState: Section[], newState: Section[], source: ActionSource): any[] {
    switch (action.type) {
      case 'append-block': {
        const lastSection = newState[newState.length - 1];
        return [
          {
            type: 'set-step-blocks',
            payload: {
              step_uuid: lastSection.id,
              json: JSON.stringify(sectionToJNodes(lastSection, true)),
            },
          },
        ];
      }
      case 'insert-block-at-boundary': {
        const section = newState[action.boundary.sectionIndex];
        return [
          {
            type: 'set-step-blocks',
            payload: {
              step_uuid: section.id,
              json: JSON.stringify(sectionToJNodes(section, true)),
            },
          },
        ];
      }
      case 'move-block-to-boundary': {
        const fromSectionIndex = findSectionIndexByBlockId(oldState, action.id)!;
        const toSectionIndex = findSectionIndexByBlockId(newState, action.id)!;

        const toSection = newState[toSectionIndex];
        const actions = [
          {
            type: 'set-step-blocks',
            payload: {
              step_uuid: toSection.id,
              json: JSON.stringify(sectionToJNodes(toSection, true)),
            },
          },
        ];
        if (fromSectionIndex != toSectionIndex) {
          const fromSection = newState[fromSectionIndex];
          actions.push({
            type: 'set-step-blocks',
            payload: {
              step_uuid: fromSection.id,
              json: JSON.stringify(sectionToJNodes(fromSection, true)),
            },
          });
        }

        return actions;
      }
      case 'resize-horizontal':
      case 'resize-vertical': {
        const section = newState[action.boundary.sectionIndex];
        return [
          {
            type: 'set-step-blocks',
            payload: {
              step_uuid: section.id,
              json: JSON.stringify(sectionToJNodes(section, true)),
            },
          },
        ];
      }
      case 'replace-block':
      case 'set-block-vertical-alignment':
      case 'update-block-content': {
        const section = findSectionByBlockId(newState, action.id)!;
        return [
          {
            type: 'set-step-blocks',
            payload: {
              step_uuid: section.id,
              json: JSON.stringify(sectionToJNodes(section, true)),
            },
          },
        ];
      }
      case 'delete-block': {
        const index = findSectionIndexByBlockId(oldState, action.id)!;
        const section = newState[index];
        return [
          {
            type: 'set-step-blocks',
            payload: {
              step_uuid: section.id,
              json: JSON.stringify(sectionToJNodes(section, true)),
            },
          },
        ];
      }
      case 'set-step-blocks': {
        return [
          {
            type: 'set-step-blocks',
            payload: {
              step_uuid: action.id,
              json: action.json,
            },
          },
        ];
      }
      case 'append-section': {
        let previousSectionId = null;
        const section = newState.find((s) => s.id === action.id)!;
        if (newState.length >= 2) {
          previousSectionId = newState[newState.length - 2].id;
        }
        return [
          {
            type: 'create-step',
            payload: {
              step_uuid: action.id,
              step_name: section.name,
              previous_step_uuid: previousSectionId,
            },
          },
          {
            type: 'set-step-blocks',
            payload: {
              step_uuid: action.id,
              json: JSON.stringify(sectionToJNodes(section, true)),
            },
          },
        ];
      }
      case 'delete-section': {
        return [
          {
            type: 'delete-step',
            payload: {
              step_uuid: action.id,
            },
          },
        ];
      }
      case 'undelete-section': {
        let previousSectionId = null;
        if (action.index > 0) {
          previousSectionId = newState[action.index - 1].id;
        }
        return [
          {
            type: 'undelete-step',
            payload: {
              step_uuid: action.section.id,
              step_name: action.section.name,
              previous_step_uuid: previousSectionId,
            },
          },
        ];
      }
      case 'move-section': {
        const { newIndex } = action;
        let newPreviousStepUuid = null;
        if (newIndex > 0) {
          newPreviousStepUuid = newState[newIndex - 1].id;
        }
        return [
          {
            type: 'move-step',
            payload: {
              step_uuid: action.id,
              new_previous_step_uuid: newPreviousStepUuid,
            },
          },
        ];
      }
      case 'merge-sections': {
        const previousSectionIndex = findSectionIndexById(oldState, action.previousSectionId)!;
        const resultingNewSection = newState[previousSectionIndex];

        return [
          {
            type: 'set-step-blocks',
            payload: {
              step_uuid: action.previousSectionId,
              json: JSON.stringify(sectionToJNodes(resultingNewSection, true)),
            },
          },
          {
            type: 'delete-step',
            payload: {
              step_uuid: action.nextSectionId,
            },
          },
        ];
      }
      case 'unmerge-sections': {
        const newPreviousSection = findSectionById(newState, action.previousSectionId)!;
        return [
          {
            type: 'set-step-blocks',
            payload: {
              step_uuid: action.previousSectionId,
              json: JSON.stringify(sectionToJNodes(newPreviousSection, true)),
            },
          },
          {
            type: 'undelete-step',
            payload: {
              step_uuid: action.nextSection.id,
              step_name: action.nextSection.name,
              previous_step_uuid: action.previousSectionId,
            },
          },
          {
            type: 'set-step-blocks',
            payload: {
              step_uuid: action.nextSection.id,
              json: JSON.stringify(sectionToJNodes(action.nextSection, true)),
            },
          },
        ];
      }
      case 'insert-section-at-boundary': {
        const splitHappened = newState.length - oldState.length === 2;
        const sectionIndex = findSectionIndexById(newState, action.id)!;

        if (splitHappened) {
          const [section1, section2, section3] = newState.slice(sectionIndex - 1, sectionIndex + 2);
          return [
            {
              type: 'set-step-blocks',
              payload: {
                step_uuid: section1.id,
                json: JSON.stringify(sectionToJNodes(section1, true)),
              },
            },
            {
              type: 'create-step',
              payload: {
                step_uuid: section2.id,
                step_name: section2.name,
                previous_step_uuid: section1.id,
              },
            },
            {
              type: 'set-step-blocks',
              payload: {
                step_uuid: section2.id,
                json: JSON.stringify(sectionToJNodes(section2, true)),
              },
            },
            {
              type: 'create-step',
              payload: {
                step_uuid: section3.id,
                step_name: section3.name,
                previous_step_uuid: section2.id,
              },
            },
            {
              type: 'set-step-blocks',
              payload: {
                step_uuid: section3.id,
                json: JSON.stringify(sectionToJNodes(section3, true)),
              },
            },
          ];
        } else {
          const newSection = newState[sectionIndex];
          const previousSectionId = sectionIndex > 0 ? newState[sectionIndex - 1].id : null;
          return [
            {
              type: 'create-step',
              payload: {
                step_uuid: newSection.id,
                step_name: newSection.name,
                previous_step_uuid: previousSectionId,
              },
            },
            {
              type: 'set-step-blocks',
              payload: {
                step_uuid: newSection.id,
                json: JSON.stringify(sectionToJNodes(newSection, true)),
              },
            },
          ];
        }
      }
      case 'uninsert-section-at-boundary': {
        if (action.joinSections) {
          const newOriginalSection = newState[action.boundary.sectionIndex];
          const oldNewSection = oldState[action.boundary.sectionIndex + 1];
          const oldSecondHalfSection = oldState[action.boundary.sectionIndex + 2];

          return [
            {
              type: 'set-step-blocks',
              payload: {
                step_uuid: newOriginalSection.id,
                json: JSON.stringify(sectionToJNodes(newOriginalSection, true)),
              },
            },
            {
              type: 'delete-step',
              payload: {
                step_uuid: oldNewSection.id,
              },
            },
            {
              type: 'delete-step',
              payload: {
                step_uuid: oldSecondHalfSection.id,
              },
            },
          ];
        } else {
          return [
            {
              type: 'delete-step',
              payload: {
                step_uuid: action.id,
              },
            },
          ];
        }
      }
      case 'move-section-divider-to-boundary': {
        return [];
      }
      case 'set-section-name': {
        return [
          {
            type: 'set-step-name',
            payload: {
              step_uuid: action.id,
              name: action.name,
            },
          },
        ];
      }
      default: {
        const exhaustiveCheck: never = action;
        throw new Error(`Unhandled case: ${exhaustiveCheck}`);
      }
    }
  },
  clearLastActionWasUndoOrRedo: () => {
    set({ lastActionWasUndoOrRedo: false });
  },
  initEditor: (jDoc) => {
    // console.log('initEditor', jDoc);
    const sections = jDocToBlocks(jDoc);
    lm.initializeSections(sections);
    set({ jdoc: jDoc, latestTrackableSections: sections });
  },
  resetEditor() {
    set(createInitialState());
    lm.reset();
  },
  setOffline() {
    set({ online: false });
  },
  setOnline() {
    set({ online: true });
  },
}));

// useEditorStore.subscribe((state) => {
//   console.log('useEditorStore changed', state);
// });

// @ts-ignore
window.editorStore = useEditorStore;
